import { Head, Form, Link } from '@inertiajs/react'
import type { Organization } from '@/types'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'

type Labels = {
  title: string
  back_link: string
  name: string
  keg_prefix: string
  keg_prefix_help: string
  keg_prefix_locked_help: string
  keg_prefix_suggested_hint: string
  contact_email: string
  contact_email_help: string
  contact_phone: string
  submit: string
}

type Props = {
  organization: Organization
  suggested_keg_prefix: string
  keg_prefix_locked: boolean
  labels: Labels
}

export default function OrganizationEdit({ organization, suggested_keg_prefix, keg_prefix_locked, labels }: Props) {
  const prefixHint = labels.keg_prefix_help + (organization.keg_prefix ? '' : labels.keg_prefix_suggested_hint)

  return (
    <>
      <Head title={labels.title} />
      <div className="mx-auto max-w-xl px-4 py-10 sm:px-6 lg:px-8">
        <Link href={`/organizations/${organization.slug}`} className="text-sm font-semibold text-ink-soft hover:text-ink">
          {labels.back_link}
        </Link>
        <h1 className="mt-4 font-display text-3xl text-ink">{labels.title}</h1>

        <Card className="mt-6 p-6 sm:p-8">
          <Form method="patch" action={`/organizations/${organization.slug}`} className="space-y-5">
            {({ errors, processing }) => (
              <>
                <Field label={labels.name} name="name" type="text" defaultValue={organization.name} required autoFocus error={errors.name} />
                {/* Once kegs carry it, the prefix is a fact about the brewery, not a setting —
                    showing an input the server will always refuse would be a trap. */}
                {keg_prefix_locked ? (
                  <div>
                    <span className="block text-sm font-semibold text-ink">{labels.keg_prefix}</span>
                    <p className="mt-1.5 font-display text-2xl text-ink">{organization.keg_prefix}</p>
                    <p className="mt-1 text-sm text-ink-soft">{labels.keg_prefix_locked_help}</p>
                  </div>
                ) : (
                  <Field
                    label={labels.keg_prefix}
                    name="keg_prefix"
                    type="text"
                    hint={prefixHint}
                    defaultValue={organization.keg_prefix ?? suggested_keg_prefix}
                    maxLength={8}
                    error={errors.keg_prefix}
                  />
                )}
                <Field
                  label={labels.contact_email}
                  name="contact_email"
                  type="email"
                  hint={labels.contact_email_help}
                  defaultValue={organization.contact_email ?? ''}
                  error={errors.contact_email}
                />
                <Field
                  label={labels.contact_phone}
                  name="contact_phone"
                  type="tel"
                  defaultValue={organization.contact_phone ?? ''}
                  error={errors.contact_phone}
                />

                <Button type="submit" disabled={processing} className="w-full disabled:opacity-60">
                  {labels.submit}
                </Button>
              </>
            )}
          </Form>
        </Card>
      </div>
    </>
  )
}
